import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { ICONS } from 'consts/consts';
import { isEqual } from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { getFeedbackVisibilityType } from 'utils/models/Feedback';
import { useIsSuperAdmin } from 'utils/models/User';
import { useCurrentOrganization } from 'utils/util/hooks';
import { formatDateWithUnicode, getDatePart } from 'utils/util/time';
import {
  autoDownloadLink,
  getPrettyDate,
  sanitizeUrlQueryParams,
} from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import FeedbackRequestCard from 'views/Feedback/FeedbackRequestCard';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import {
  getPeopleAdminableOrSelfQuery,
  getPeopleOrAllQuery,
  INPUT_TYPES,
} from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import {
  FORMAT_AVATAR_GROUP,
  FORMAT_AVATAR_ONLY,
} from 'views/Widgets/People/Filters/common';
import { AvatarBasicPerson } from 'views/Widgets/People/UnlinkedAvatar';
import RevealLink from 'views/Widgets/RevealLink';
import DeleteModal from './DeleteModal';
import EditModal from './EditModal';
import FeedbackCompletedOverlay from './FeedbackCompletedOverlay';
import FeedbackDeclinedOverlay from './FeedbackDeclinedOverlay';
import MoreActionsButton from './MoreActionsButton';
import UndeleteModal from './UndeleteModal';

const declineOverlayCallback = (ap: AvatarBasicPerson) => {
  return <FeedbackDeclinedOverlay ap={ap} />;
};

const completedOverlayCallback = (ap: AvatarBasicPerson) => {
  return <FeedbackCompletedOverlay ap={ap} />;
};

const ReceivedFeedbackAndRecognitionAdminPage: FC = () => {
  const { formatMessage, locale } = useIntl();
  const [isEditFeedbackRequestModalOpen, setIsEditFeedbackRequestModalOpen] =
    useState(false);
  const [
    isDeleteFeedbackRequestModalOpen,
    setIsDeleteFeedbackRequestModalOpen,
  ] = useState(false);
  const [isUndeleteFeedbackModalOpen, setIsUndeleteFeedbackModalOpen] =
    useState(false);
  const [editableFeedbackRequestId, setEditableFeedbackRequestId] = useState<
    number | undefined
  >(undefined);
  const [deletableFeedbackRequestId, setDeletableFeedbackRequestId] = useState<
    number | undefined
  >(undefined);
  const [undeletableFeedbackRequestId, setUndeletableFeedbackRequestId] =
    useState<number | undefined>(undefined);
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);

  const isSuperAdmin = useIsSuperAdmin();
  const currentOrganization = useCurrentOrganization();

  const handleOnEditClick = useCallback((feedbackRequestId: number) => {
    setEditableFeedbackRequestId(feedbackRequestId);
    setIsEditFeedbackRequestModalOpen(true);
  }, []);

  const handleOnDeleteClick = useCallback((feedbackRequestId: number) => {
    setDeletableFeedbackRequestId(feedbackRequestId);
    setIsDeleteFeedbackRequestModalOpen(true);
  }, []);

  const handleOnUndeleteClick = useCallback((feedbackRequestId: number) => {
    setUndeletableFeedbackRequestId(feedbackRequestId);
    setIsUndeleteFeedbackModalOpen(true);
  }, []);

  const handleToggleEditModal = useCallback(() => {
    setIsEditFeedbackRequestModalOpen(false);
    setEditableFeedbackRequestId(undefined);
  }, []);

  const updateFeedbackOnAccumulatedData = useCallback(
    (editedFeedbackRequest) => {
      setAccumulatedData((ad) =>
        ad?.map((feedbackRequest) =>
          feedbackRequest.id === editedFeedbackRequest.id
            ? editedFeedbackRequest
            : feedbackRequest
        )
      );
    },
    []
  );

  const handleCallbackEditModal = useCallback(
    (editedFeedbackRequest) => {
      updateFeedbackOnAccumulatedData(editedFeedbackRequest);
      handleToggleEditModal();
    },
    [handleToggleEditModal, updateFeedbackOnAccumulatedData]
  );

  const handleToggleDeleteModal = useCallback(() => {
    setIsDeleteFeedbackRequestModalOpen(false);
    setDeletableFeedbackRequestId(undefined);
  }, []);

  const handleCallbackDeleteModal = useCallback(
    (deletedFeedbackRequest) => {
      updateFeedbackOnAccumulatedData(deletedFeedbackRequest);
      handleToggleDeleteModal();
    },
    [handleToggleDeleteModal, updateFeedbackOnAccumulatedData]
  );

  const handleToggleUndeleteModal = useCallback(() => {
    setIsUndeleteFeedbackModalOpen(false);
    setUndeletableFeedbackRequestId(undefined);
  }, []);

  const handleCallbackUndeleteModal = useCallback(
    (undeletedFeedbackRequest) => {
      updateFeedbackOnAccumulatedData(undeletedFeedbackRequest);
      handleToggleUndeleteModal();
    },
    [handleToggleUndeleteModal, updateFeedbackOnAccumulatedData]
  );

  const handleFilteringCallback = useCallback(() => {}, []);

  const [currentUrl, setCurrentUrl] = useState<string>(
    'admin/feedback-requests'
  );
  const [currentTotalCount, setCurrentTotalCount] = useState<
    number | undefined
  >(undefined);
  const [currentParams, setCurrentParams] = useState<{
    type?: string;
    relevant_people_ids?: string;
  }>({});
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [accumulatedData, setAccumulatedData] = useState<any[] | undefined>(
    undefined
  );
  const [filteringInProgress, setFilteringInProgress] = useState(false);
  const [downloadingCSVInProgress, setDownloadingCSVInProgress] =
    useState(false);

  const callback = useCallback(
    (
      data: { results: any[]; next?: string; count: number } | null,
      error,
      hardError
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching feedback data:', error || hardError);
        return;
      }
      setAccumulatedData((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setCurrentTotalCount(data.count);
      setFilteringInProgress(false);
      setIsLoadingMoreItems(false);
    },
    []
  );
  const { error } = useConfirmApi<{
    results: any[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    params: currentParams,
    callback: callback,
  });

  const [csvTaskDescriptor, setCsvTaskDescriptor] = useState<
    { task_key: string; status: string } | undefined
  >(undefined);

  const handleCsvExportError = useCallback(
    (error: object) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      console.error('Error downloading CSV:', error);
      toast.error(
        formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.error_generating_csv',
          defaultMessage: 'Error generating CSV',
        })
      );
    },
    [formatMessage]
  );

  const handleCsvExportCompleted = useCallback(
    (output: BlobPart) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      autoDownloadLink({
        data: output,
        filename: `feedback_${
          currentOrganization?.name ?? 'confirm'
        }_${formatDateWithUnicode(new Date(), 'yyyy_MM_dd_hh_mm_ss')}.csv`,
        mime: 'text/csv',
      });
    },
    [currentOrganization?.name]
  );

  useBackgroundTask({
    taskDescriptor: csvTaskDescriptor,
    onTaskCompleted: handleCsvExportCompleted,
    onError: handleCsvExportError,
  });

  const onCsvExportSubmitted = useCallback(
    (data: { task_key?: string } | null, error) => {
      if (error || !data?.task_key) {
        handleCsvExportError(error);
        return;
      }
      setCsvTaskDescriptor({ task_key: data.task_key, status: 'submitted' });
    },
    [handleCsvExportError]
  );

  const handleOnDownloadCSV = useCallback(() => {
    setDownloadingCSVInProgress(true);
  }, []);

  // This request only enqueues the export; polling delivers the file.
  useConfirmApi({
    method: 'GET',
    url: sanitizeUrlQueryParams(currentUrl),
    params: {
      ...currentParams,
      page: undefined,
      media_type: 'text/csv',
    },
    disabled: !downloadingCSVInProgress || !!csvTaskDescriptor,
    callback: onCsvExportSubmitted,
  });

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.created_at',
          defaultMessage: 'Date',
        }),
        field: 'created_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            locale,
            includeTime: false,
          });
        },
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.requester_person',
          defaultMessage: 'Requester',
        }),
        field: 'requester_person',
        format: FORMAT_AVATAR_ONLY,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.subject_people',
          defaultMessage: 'Subjects',
        }),
        field: 'subject_people',
        className: 'col-1',
        format: FORMAT_AVATAR_GROUP,
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.requested_people',
          defaultMessage: 'Requestees',
        }),
        field: 'requested_people',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.pending_people',
          defaultMessage: 'Pending',
        }),
        field: 'pending_people',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.declined_people',
          defaultMessage: 'Declined',
        }),
        field: 'declined_people',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.completed_people',
          defaultMessage: 'Completed',
        }),
        field: 'completed_people',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.status',
          defaultMessage: 'Status',
        }),
        field: 'deleted_at',
        className: 'col-1',
        format: (deleted_at) => {
          return deleted_at
            ? formatMessage({
                id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.deleted_at.value.deleted',
                defaultMessage: 'Deleted',
              })
            : formatMessage({
                id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.deleted_at.value.live',
                defaultMessage: 'Live',
              });
        },
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.body',
          defaultMessage: 'Body',
        }),
        field: 'body',
        className: 'col-auto',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.requested.table.column.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    if (!accumulatedData) {
      return undefined;
    }
    return accumulatedData.map((feedbackRequest) => {
      const visibility = getFeedbackVisibilityType(
        feedbackRequest,
        formatMessage
      );
      const declinedPeople = feedbackRequest.feedback_declines.map((item) => ({
        ...item.author_person,
        declined_date: item.created_at,
        declined_comments: item.comments,
        overlayCallback: declineOverlayCallback,
      }));
      const completedPeople = feedbackRequest.feedback.map((item) => ({
        ...item.author_person,
        completed_date: item.created_at,
        overlayCallback: completedOverlayCallback,
      }));
      const requestedPeople = feedbackRequest.requested_people;

      const answeredPeopleIds = new Set();
      completedPeople.forEach((person) => {
        answeredPeopleIds.add(person.id);
      });
      declinedPeople.forEach((person) => {
        answeredPeopleIds.add(person.id);
      });

      const pendingPeople = requestedPeople.filter(
        (person) => !answeredPeopleIds.has(person.id)
      );

      return {
        id: feedbackRequest.id,
        created_at: feedbackRequest.created_at,
        deleted_at: feedbackRequest.deleted_at,
        // @ts-expect-error
        visibility: visibility.name,
        requester_person: feedbackRequest.author_person,
        subject_people: feedbackRequest.subject_people,
        pending_people: pendingPeople,
        requested_people: requestedPeople,
        declined_people: declinedPeople,
        completed_people: completedPeople,
        body: (
          <FeedbackRequestCard
            className=""
            feedbackRequest={feedbackRequest}
            showAvatar={false}
          />
        ),
        actions: (
          <MoreActionsButton
            feedbackRequest={feedbackRequest}
            onEditClick={handleOnEditClick}
            onDeleteClick={handleOnDeleteClick}
            onUndeleteClick={handleOnUndeleteClick}
          />
        ),
      };
    });
  }, [
    accumulatedData,
    formatMessage,
    handleOnDeleteClick,
    handleOnEditClick,
    handleOnUndeleteClick,
  ]);

  const renderInputs = useCallback(
    ([sender, startDate, endDate]: ReactNode[]) => {
      return (
        <Row>
          <Col className="col-6">
            <Row>
              <Col className="ps-0">{sender}</Col>
              <Col className="col-auto">
                <div className="text-muted mt-2 pt-1">
                  <i className="fe fe-filter me-2" />
                </div>
              </Col>
            </Row>
            <Row>
              <RevealLink
                className="ps-0 ms-0 mt-n3 mb-2 pb-2 pt-0"
                text={formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.requested.search.reveal_link.additional_search_options',
                  defaultMessage: 'Additional search options...',
                })}
              >
                <Col className="ps-0">{startDate}</Col>
                <Col className="ps-0">{endDate}</Col>
              </RevealLink>
            </Row>
          </Col>
          <Col className="col-6">
            {!!currentTotalCount && (
              <div className="float-right">
                <Button
                  onClick={handleOnDownloadCSV}
                  className="btn btn-sm btn-light float-end"
                >
                  <i className={ICONS.EXPORT + ' me-2'} />
                  <FormattedMessage
                    id="app.views.administration.content_administration.feedback_and_recognition_admin.requested.page.export_to_csv"
                    defaultMessage="Export {count, plural, one {# item} other {# items}} to CSV"
                    values={{ count: currentTotalCount }}
                  />
                </Button>
              </div>
            )}
          </Col>
        </Row>
      );
    },
    [currentTotalCount, formatMessage, handleOnDownloadCSV]
  );

  const handleOnChangeFilter = useCallback(
    (object) => {
      const { relevant_people, start_date, end_date } = object;
      const params = {
        relevant_people_ids: (relevant_people ?? [])
          .map((it) => it.id)
          .join(','),
        start_date: start_date ? getDatePart(start_date) : undefined,
        end_date: end_date ? getDatePart(end_date) : undefined,
      };
      if (!isEqual(currentParams, params)) {
        setFilteringInProgress(true);
        setIsLoadingMoreItems(false);
        setAccumulatedData(undefined);
        setCurrentParams(params);
        setCurrentUrl('admin/feedback-requests');
        setNextUrl(undefined);
      }
    },
    [currentParams]
  );

  const adminablePersonElasticSearchOptions = useMemo(() => {
    return {
      getQuery: isSuperAdmin
        ? getPeopleOrAllQuery
        : getPeopleAdminableOrSelfQuery,
    };
  }, [isSuperAdmin]);

  const actions = useMemo(() => {
    return (
      <Card>
        <CardBody className="pt-4 pb-0 mb-0">
          <ValidatedForm
            renderInputs={renderInputs}
            buttonColor="light"
            hideSubmitButton={true}
            disableUnsavedChangesPrompt={true}
            onChange={handleOnChangeFilter}
            callback={handleFilteringCallback}
            inputs={[
              {
                name: 'relevant_people',
                type: INPUT_TYPES.PEOPLE_EDITOR,
                allowSelf: true,
                placeholder: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.requested.search_by_people',
                  defaultMessage: 'Search by requester, requestees or subjects',
                }),
                elasticsearchOptions: adminablePersonElasticSearchOptions,
              },
              {
                name: 'start_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.requested.search.start_date.helper_text',
                  defaultMessage: 'Start date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
              {
                name: 'end_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.requested.search.end_date.helper_text',
                  defaultMessage: 'End date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
            ]}
          />
        </CardBody>
      </Card>
    );
  }, [
    renderInputs,
    handleOnChangeFilter,
    handleFilteringCallback,
    formatMessage,
    adminablePersonElasticSearchOptions,
  ]);

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <>
      <Loading
        active={filteringInProgress || !rows}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.loading.message.loading_data',
          defaultMessage: 'Loading data...',
        })}
      />
      <Loading
        active={downloadingCSVInProgress}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.loading.message.downloading_csv',
          defaultMessage: 'Downloading CSV, please wait...',
        })}
      />
      {editableFeedbackRequestId && (
        <EditModal
          isOpen={isEditFeedbackRequestModalOpen}
          feedbackRequestId={editableFeedbackRequestId}
          toggle={handleToggleEditModal}
          callback={handleCallbackEditModal}
        />
      )}
      {deletableFeedbackRequestId && (
        <DeleteModal
          isOpen={isDeleteFeedbackRequestModalOpen}
          feedbackRequestId={deletableFeedbackRequestId}
          onClosed={handleToggleDeleteModal}
          onDelete={handleCallbackDeleteModal}
        />
      )}
      {undeletableFeedbackRequestId && (
        <UndeleteModal
          isOpen={isUndeleteFeedbackModalOpen}
          feedbackRequestId={undeletableFeedbackRequestId}
          onClosed={handleToggleUndeleteModal}
          onUndelete={handleCallbackUndeleteModal}
        />
      )}
      {actions}
      {!!rows && (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
        />
      )}
      <div ref={loadMoreRef} />
      {isLoadingMoreItems && <Loading />}
    </>
  );
};

export default React.memo(ReceivedFeedbackAndRecognitionAdminPage);
